import { makeAutoObservable, runInAction } from 'mobx';
import { computerProductionUpgradePolicyApi } from '@/services/computerProductionUpgradePolicyApi';
import type {
  ChangeComputerProductionUpgradePolicyRequest,
  ComputerProductionUpgradeApproval,
  GetComputerProductionUpgradePolicyReply,
} from '@/types';

interface ApiErrorLike {
  message?: string;
  response?: {
    data?: { error?: string; errmsg?: string; message?: string };
  };
}

function errorMessage(error: unknown, fallback: string) {
  if (typeof error !== 'object' || error === null) {
    return fallback;
  }
  const candidate = error as ApiErrorLike;
  return (
    candidate.response?.data?.errmsg ||
    candidate.response?.data?.message ||
    candidate.response?.data?.error ||
    candidate.message ||
    fallback
  );
}

function compareApprovalIDsDescending(left: string, right: string) {
  if (left.length !== right.length) {
    return right.length - left.length;
  }
  return right.localeCompare(left);
}

function mergeApprovals(
  preferred: ComputerProductionUpgradeApproval[],
  retained: ComputerProductionUpgradeApproval[],
) {
  const approvals = new Map(
    retained.map((approval) => [approval.id, approval] as const),
  );
  for (const approval of preferred) {
    approvals.set(approval.id, approval);
  }
  return [...approvals.values()].sort((left, right) =>
    compareApprovalIDsDescending(left.id, right.id),
  );
}

class ComputerProductionUpgradePolicyStore {
  loading = false;
  saving = false;
  error = '';
  snapshot: GetComputerProductionUpgradePolicyReply | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async fetch(
    options: { appendApprovals?: boolean; preserveHistory?: boolean } = {},
  ) {
    this.loading = true;
    this.error = '';
    const current = this.snapshot;
    try {
      const response = await computerProductionUpgradePolicyApi.get(
        options.appendApprovals ? current?.nextApprovalPageToken || '' : '',
      );
      runInAction(() => {
        if (current && options.appendApprovals) {
          response.approvals = mergeApprovals(
            response.approvals || [],
            current.approvals,
          );
        } else if (current && options.preserveHistory) {
          response.approvals = mergeApprovals(
            response.approvals || [],
            current.approvals.filter(
              (approval) =>
                approval.status !== 'pending' && approval.status !== 'approved',
            ),
          );
          response.nextApprovalPageToken = current.nextApprovalPageToken;
        }
        this.snapshot = response;
        this.loading = false;
      });
    } catch (error) {
      const message = errorMessage(
        error,
        '加载 Computer Production 强制升级策略失败',
      );
      runInAction(() => {
        this.loading = false;
        this.error = message;
      });
      throw new Error(message);
    }
  }

  async fetchMoreApprovals() {
    if (!this.snapshot?.nextApprovalPageToken || this.loading) {
      return;
    }
    await this.fetch({ appendApprovals: true });
  }

  async change(request: ChangeComputerProductionUpgradePolicyRequest) {
    this.saving = true;
    try {
      const response = await computerProductionUpgradePolicyApi.change(request);
      await this.fetch();
      return response;
    } catch (error) {
      await this.fetch().catch(() => undefined);
      throw new Error(
        errorMessage(error, '提交 Computer Production 强制升级策略失败'),
      );
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  async approve(approvalId: string) {
    return this.review(async () =>
      computerProductionUpgradePolicyApi.approve(approvalId),
    );
  }

  async reject(approvalId: string, reason: string) {
    return this.review(async () =>
      computerProductionUpgradePolicyApi.reject(approvalId, reason),
    );
  }

  async withdraw(approvalId: string) {
    return this.review(async () =>
      computerProductionUpgradePolicyApi.withdraw(approvalId),
    );
  }

  private async review<T>(operation: () => Promise<T>) {
    this.saving = true;
    try {
      const response = await operation();
      await this.fetch();
      return response;
    } catch (error) {
      await this.fetch().catch(() => undefined);
      throw new Error(
        errorMessage(error, '处理 Computer Production 策略审批失败'),
      );
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }
}

export const computerProductionUpgradePolicyStore =
  new ComputerProductionUpgradePolicyStore();
